import '@blocknote/core/fonts/inter.css'
import { BlockNoteView } from '@blocknote/mantine'
import '@blocknote/mantine/style.css'
import ctl from '@netlify/classnames-template-literals'

import { BlockNoteEditor, filterSuggestionItems } from '@blocknote/core'
import {
	DefaultReactSuggestionItem,
	getDefaultReactSlashMenuItems,
	SuggestionMenuController,
	useCreateBlockNote,
} from '@blocknote/react'
import { BoldIcon, Heading2Icon, ItalicIcon, ListIcon, ListOrderedIcon, UnderlineIcon } from 'lucide-react'
import { CSSProperties, forwardRef, useCallback, useEffect, useImperativeHandle, useRef } from 'react'
import { useTranslation } from 'react-i18next'

interface RichLiveFieldProps {
	value: string
	onChange: (value: string) => void
	isGenerating?: boolean
	disableMedia?: boolean
	readOnly?: boolean
	onCmdEnter?: () => void
	outputFormat?: 'json' | 'html'
	showStaticToolbar?: boolean
}

export interface RichLiveFieldRef {
	rerender: () => void
	insertText: (text: string) => void
}

export const RichLiveField = forwardRef<RichLiveFieldRef, RichLiveFieldProps>(({
	value,
	onChange,
	isGenerating = false,
	disableMedia = false,
	readOnly = false,
	onCmdEnter,
	outputFormat = 'json',
	showStaticToolbar = false,
}, ref) => {
	const { t } = useTranslation('common')
	const editor = useCreateBlockNote()

	const toggleStyle = useCallback((style: 'bold' | 'italic' | 'underline') => {
		editor.toggleStyles({ [style]: true })
		editor.focus()
	}, [editor])

	const toggleBlockType = useCallback((type: 'heading' | 'bulletListItem' | 'numberedListItem') => {
		const currentBlock = editor.getTextCursorPosition().block
		if (currentBlock.type === type || (type === 'heading' && currentBlock.type === 'heading')) {
			editor.updateBlock(currentBlock, { type: 'paragraph' })
		} else if (type === 'heading') {
			editor.updateBlock(currentBlock, { type: 'heading', props: { level: 2 } })
		} else {
			editor.updateBlock(currentBlock, { type })
		}
		editor.focus()
	}, [editor])

	const isLoadingContentRef = useRef(false)
	const lastLoadedValueRef = useRef<string | null>(null)

	async function loadContents(newValue: string) {
		isLoadingContentRef.current = true
		try {
			let blocks
			if (outputFormat === 'html') {
				// For HTML format, parse HTML to blocks
				blocks = await editor.tryParseHTMLToBlocks(newValue)
			} else {
				try {
					blocks = JSON.parse(newValue)
				} catch {
					// If parsing fails, assume the value is a markdown string
					// and convert it to block note document
					blocks = await editor.tryParseMarkdownToBlocks(newValue)
				}
			}
			editor.replaceBlocks(editor.document, blocks)
			lastLoadedValueRef.current = newValue
		} finally {
			isLoadingContentRef.current = false
		}
	}

	const omittedMenuItems = [
		'Toggle Heading 1',
		'Toggle Heading 2',
		'Toggle Heading 3',
		'File',
		'Audio',
		'Code Block',
		'Table',
		...(disableMedia ? ['Image', 'Video'] : []),
	]

	const getCustomSlashMenuItems = (
		editor: BlockNoteEditor,
	): DefaultReactSuggestionItem[] => [
		...getDefaultReactSlashMenuItems(editor).filter((item) => !omittedMenuItems.includes(item.title)),
	]

	useEffect(() => {
		if (isGenerating || !value) {
			return
		}

		if (lastLoadedValueRef.current === null || value !== lastLoadedValueRef.current) {
			loadContents(value)
		}
	}, [editor, value, isGenerating])

	useImperativeHandle(ref, () => ({
		rerender: () => {
			lastLoadedValueRef.current = null
		},
		insertText: (text: string) => {
			// @ts-ignore -- BlockNote exposes TipTap editor internally
			editor._tiptapEditor.commands.insertContent(text)
			editor.focus()
		},
	}), [editor])

	if (isGenerating) {
		return <div className="h-[1em] w-full min-w-10 bg-grey-200 rounded-full animate-pulse" />
	}

	return (
		<div
			className={ctl(`
				group
				rich-live-text
				relative
				${
				!readOnly && `
					hover:ring-4 hover:ring-white
					hover:outline hover:outline-black
					focus-within:ring-4 focus-within:ring-white
					focus-within:outline focus-within:outline-black
					focus-within:bg-white focus-within:text-black focus-within:outline-none
				`
			}
				${
				readOnly && `
					bg-grey-50
					!cursor-default
					[&_*]:!bg-transparent
				`
			}
			`)}
			style={{
				// for future overriding with custom fonts
				'--rich-live-text-font-family': 'var(--font-sans)',
			} as CSSProperties}
			onKeyDown={(e) => {
				if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
					e.preventDefault()
					onCmdEnter?.()
				}
			}}
		>
			{showStaticToolbar && !readOnly && (
				<div className="flex items-center gap-1 px-2 py-1.5 border-b border-gray-200 bg-gray-50">
					<ToolbarButton
						icon={<BoldIcon className="size-4" />}
						label={t('richText.bold')}
						onClick={() => toggleStyle('bold')}
					/>
					<ToolbarButton
						icon={<ItalicIcon className="size-4" />}
						label={t('richText.italic')}
						onClick={() => toggleStyle('italic')}
					/>
					<ToolbarButton
						icon={<UnderlineIcon className="size-4" />}
						label={t('richText.underline')}
						onClick={() => toggleStyle('underline')}
					/>
					<div className="w-px h-4 bg-gray-300 mx-1" />
					<ToolbarButton
						icon={<Heading2Icon className="size-4" />}
						label={t('richText.heading')}
						onClick={() => toggleBlockType('heading')}
					/>
					<ToolbarButton
						icon={<ListIcon className="size-4" />}
						label={t('richText.bulletList')}
						onClick={() => toggleBlockType('bulletListItem')}
					/>
					<ToolbarButton
						icon={<ListOrderedIcon className="size-4" />}
						label={t('richText.numberedList')}
						onClick={() => toggleBlockType('numberedListItem')}
					/>
				</div>
			)}
			<BlockNoteView
				editor={editor}
				editable={!readOnly}
				onChange={async () => {
					if (isLoadingContentRef.current) {
						return
					}
					let newValue: string
					if (outputFormat === 'html') {
						newValue = await editor.blocksToHTMLLossy(editor.document)
					} else {
						newValue = JSON.stringify(editor.document)
					}
					lastLoadedValueRef.current = newValue
					onChange(newValue)
				}}
				theme={'light'}
				slashMenu={false}
				formattingToolbar={true}
				sideMenu={false}
				linkToolbar={false}
				filePanel={false}
				tableHandles={false}
			>
				<SuggestionMenuController
					triggerCharacter={'/'}
					getItems={async (query) => filterSuggestionItems(getCustomSlashMenuItems(editor), query)}
				>
				</SuggestionMenuController>
			</BlockNoteView>
		</div>
	)
})

function ToolbarButton({
	icon,
	label,
	onClick,
}: {
	icon: React.ReactNode
	label: string
	onClick: () => void
}) {
	return (
		<button
			type="button"
			onClick={onClick}
			title={label}
			className="p-1.5 rounded text-gray-600 hover:bg-gray-200 hover:text-gray-900 transition-colors"
		>
			{icon}
		</button>
	)
}
